import React, { useCallback, useEffect, useMemo } from 'react';
import { Button } from '@appkit4/react-components/button';
import { Loading } from '@appkit4/react-components/loading';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '../Constants';
import {
    useGetAvailableTenants,
    useActivateTenant,
} from '../api/generated/endpoint';
import { useTenant } from '../context/TenantContext';
import useGenericErrorToast from '../helpers/useGenericErrorToast';
import DropDown from '../components/reusables/DropDown';
import LoadingOverlay from '../components/reusables/LoadingOverlay';
import './Engagements.scss';

const Engagements = () => {
    const navigate = useNavigate();
    const { selectTenant } = useTenant();
    const showGenericErrorToast = useGenericErrorToast();

    const { data: engagements, isLoading: loadingEngagements } =
        useGetAvailableTenants();

    const { mutate: activateTenant, isPending: isActivating } =
        useActivateTenant({
            mutation: {
                onSuccess: (data) => {
                    selectTenant({ tenantId: data.tenantId, name: data.name });
                    navigate(`/fixedasset/${data.tenantId}`);
                },
                onError: (error) => {
                    showGenericErrorToast(error);
                },
            },
        });

    const dropdownOptions = useMemo(() => {
        if (!engagements || !Array.isArray(engagements)) return [];
        return engagements.map((e) => ({
            value: String(e.tenantId),
            label: e.engName || e.name,
        }));
    }, [engagements]);

    const handleSelect = useCallback(
        (value: string | string[]) => {
            const tenantId = Number(value);
            if (!tenantId) return;
            activateTenant({ tenantId, data: { roleKey: 'CREATOR' } });
        },
        [activateTenant]
    );

    const handleAllEngagements = useCallback(() => {
        navigate(AppRoutes.Management);
    }, [navigate]);

    useEffect(() => {
        document.body.classList.add('selector-bg');
        return () => {
            document.body.classList.remove('selector-bg');
        };
    }, []);

    return (
        <div className="selector-page">
            {isActivating && <LoadingOverlay message="Opening engagement..." />}
            <div className="selector-content">
                <h1 className="selector-title">Fixed asset sorter</h1>
                <p className="selector-subtitle">
                    Select an engagement to proceed
                </p>
                <div className="selector-controls">
                    {loadingEngagements ? (
                        <div className="selector-dropdown selector-loading">
                            <Loading loadingType="circular" indeterminate />
                            <span>Loading engagements...</span>
                        </div>
                    ) : dropdownOptions.length === 0 ? (
                        <p className="selector-empty">
                            No engagements available. Please contact your
                            administrator.
                        </p>
                    ) : (
                        <DropDown
                            data={dropdownOptions}
                            placeholder="  Select..."
                            onSelect={handleSelect}
                            className="selector-dropdown"
                        />
                    )}
                    <Button
                        kind="text"
                        onClick={handleAllEngagements}
                        className="selector-all-btn"
                    >
                        All Engagements &nbsp;→
                    </Button>
                </div>
            </div>
            <footer className="selector-footer">
                <p>
                    ©{new Date().getFullYear()} PwC. All rights reserved. PwC
                    refers to the PwC network and/or one or more of its member
                    firms, each of which is a separate legal entity.
                </p>
                <p>
                    Please see{' '}
                    <a
                        href="https://www.pwc.com/structure"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        pwc.com/structure
                    </a>{' '}
                    for further details.
                </p>
            </footer>
        </div>
    );
};

export default Engagements;
